import { useState } from "react";
import { useDemo } from "../zustand/DemoZustand";
import Fraktionschat from "../ansichten/Fraktionschat";
import {
  Auge,
  Dokument,
  Etikett as EtikettSymbol,
  Haken,
  Kreuz,
  PfeilRechts,
  Raster,
  Schloss,
  Uhr,
} from "./Symbole";
import { kanalZuTop, kuerzel, nameVon } from "../hilfen/auswertung";

/*
  Eine Aufgabe, aufgeklappt.

  Der Aufbau folgt dem, was man von MeisterTask und Trello kennt, weil die
  Zielgruppe genau das schon kennt:

    ┌──────────────────────────────────────────────────────┐
    │ ✓ Abschließen        Verantwortlich: Renate      [X] │
    ├───────────────────────────────┬──────────────────────┤
    │ Titel, Beschreibung           │ Fällig               │
    │ Schritte                      │ Spalte               │
    │ Besprechung                   │ Etiketten            │
    │                               │ Beobachtung          │
    │                               │ Vorgang              │
    └───────────────────────────────┴──────────────────────┘

  Links steht, woran gearbeitet wird; rechts, was über die Aufgabe bekannt
  ist. Auf schmalen Geräten rutscht die rechte Spalte unter die linke — das
  iPad im Querformat ist der Maßstab, das Handy muss trotzdem gehen.

  Bewusst NICHT übernommen: die Zeiterfassung (das Produkt misst keine
  Arbeitszeit von Ehrenamtlichen), Unteraufgaben als eigene Aufgaben (dafür
  gibt es die Schritte), Abhängigkeiten und Anhänge. Und die Etiketten
  bleiben farblos — siehe VEREINFACHUNGEN.md.

  Warum ausgerechnet hier ein Gespräch hingehört, steht in KOMMUNIKATION.md:
  Am Dokument wird notiert und nicht geantwortet, aber „Wer fragt beim
  Bauamt nach?“ ist ohne Antwort sinnlos.

  Das Gespräch läuft nicht in einem eigenen Raum, sondern im Kanal des
  Gremiums — mit einem Verweis auf die Aufgabe an der einzelnen Nachricht.
  Ein Raum je Aufgabe hätte dieselbe Vermehrung erzeugt wie früher ein Kanal
  je Tagesordnungspunkt.
*/

export default function Aufgabenkarte({ aufgabeId, aufSchliessen, oeffne }) {
  const { zustand, stammdaten, aktionen } = useDemo();
  const aufgabe = zustand.aufgaben.find((a) => a.id === aufgabeId);

  /*
    Die Aufgabe kann weg sein — gelöscht, oder aus der Fraktionssicht
    genommen, während man noch auf den Verweis im Kanal getippt hat. Ein
    Fenster, das dann einfach nicht aufgeht, sieht aus wie ein Fehler.
  */
  if (!aufgabe) {
    return (
      <div className="ueberlagerung" role="dialog" aria-label="Aufgabe nicht gefunden">
        <div className="ueberlagerung__karte">
          <p style={{ fontWeight: 600 }}>Diese Aufgabe gibt es nicht mehr.</p>
          <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
            Sie wurde gelöscht, oder sie betrifft nur noch eine Person und steht damit nicht
            mehr im Fraktionsboard. Der Beitrag im Kanal bleibt stehen.
          </p>
          <button
            className="knopf knopf--zurueckhaltend knopf--breit"
            style={{ marginTop: "var(--luft-4)" }}
            onClick={aufSchliessen}
          >
            Schließen
          </button>
        </div>
      </div>
    );
  }

  const top = aufgabe.topId
    ? stammdaten.tagesordnung.find((t) => t.id === aufgabe.topId)
    : null;
  const kanalId = kanalZuTop(stammdaten, top);
  const mitglieder = Object.values(stammdaten.personen);
  const spalten = [...zustand.spalten].sort((a, b) => a.reihenfolge - b.reihenfolge);
  const privat = aufgabe.sichtbarkeit === "privat";

  /*
    „Abschließen“ heißt: ab in die Spalte, die als erledigt gilt. Gibt es
    keine solche Spalte, gibt es den Knopf nicht — ein Knopf, der nichts tun
    kann, ist schlimmer als keiner.
  */
  const fertigSpalte = spalten.find((s) => s.erledigt);
  const offeneSpalte = spalten.find((s) => !s.erledigt);

  function abschliessen() {
    const ziel = aufgabe.erledigt ? offeneSpalte : fertigSpalte;
    if (ziel) aktionen.aendereAufgabe(aufgabe.id, { spalteId: ziel.id });
  }

  return (
    <div className="ueberlagerung" role="dialog" aria-label={aufgabe.titel}>
      <div className="ueberlagerung__karte ueberlagerung__karte--breit aufgabenkarte">
        {/* ---------- Kopfleiste ---------- */}

        {/*
          Zwei Teile: alles Bedienbare links, das Schließkreuz rechts. Als
          eine einzige umbrechende Reihe rutschte das Kreuz am Handy auf eine
          eigene, leere Zeile — es muss oben rechts stehen bleiben, wo man es
          sucht.
        */}
        <div className="aufgabenkarte__leiste">
          <div className="aufgabenkarte__leiste-links">
            {fertigSpalte && (
              <button
                className={`knopf ${aufgabe.erledigt ? "" : "knopf--zurueckhaltend"}`}
                onClick={abschliessen}
              >
                <Haken groesse={16} />
                {aufgabe.erledigt ? "Wieder öffnen" : "Aufgabe abschließen"}
              </button>
            )}

            <Verantwortung aufgabe={aufgabe} mitglieder={mitglieder} />
          </div>

          <button
            className="knopf knopf--schlicht"
            aria-label="Schließen"
            onClick={aufSchliessen}
          >
            <Kreuz groesse={18} />
          </button>
        </div>

        <h2 className="aufgabenkarte__titel">{aufgabe.titel}</h2>

        {aufgabe.beschreibung && (
          <p className="klein leise aufgabenkarte__beschreibung">{aufgabe.beschreibung}</p>
        )}

        {privat && (
          <p className="klein leise aufgabenkarte__schloss">
            <Schloss groesse={13} /> Dein Merkzettel — niemand sonst sieht ihn. Sobald jemand
            dazukommt, ob als Verantwortliche oder als Beobachter, steht die Aufgabe im
            Fraktionsboard.
          </p>
        )}

        {/* ---------- Zwei Spalten ---------- */}

        <div className="aufgabenkarte__raster">
          <div className="aufgabenkarte__haupt">
            <Checkliste aufgabe={aufgabe} />

            <div className="aufgabenkarte__gespraech">
              <p className="aufgabenkarte__frage">Besprechung</p>

              {privat ? (
                <p className="klein leise">
                  Solange die Aufgabe nur dich betrifft, gibt es nichts zu besprechen — ein
                  Beitrag ginge in den Kanal der Fraktion, und dort läsen alle mit. Gib sie
                  jemandem oder trag jemanden als Beobachter ein, dann geht es hier weiter.
                </p>
              ) : (
                <Fraktionschat kanalId={kanalId} aufgabe={aufgabe} eingebettet />
              )}
            </div>
          </div>

          <aside className="aufgabenkarte__seite">
            <Angabe symbol={Uhr} name="Fällig">
              <input
                type="date"
                className="brett__feld"
                value={alsFeld(aufgabe.faellig)}
                onChange={(e) =>
                  aktionen.aendereAufgabe(aufgabe.id, { faellig: e.target.value || null })
                }
                aria-label="Frist"
              />
            </Angabe>

            <Angabe symbol={Raster} name="Spalte">
              <select
                className="brett__feld"
                value={aufgabe.spalteId || ""}
                onChange={(e) => aktionen.aendereAufgabe(aufgabe.id, { spalteId: e.target.value })}
                aria-label="Spalte"
              >
                {spalten.map((spalte) => (
                  <option key={spalte.id} value={spalte.id}>
                    {spalte.name}
                  </option>
                ))}
              </select>
            </Angabe>

            <Angabe symbol={EtikettSymbol} name="Etiketten">
              <Etiketten aufgabe={aufgabe} />
            </Angabe>

            <Angabe symbol={Auge} name="Beobachtung">
              <Beobachtung aufgabe={aufgabe} />
            </Angabe>

            {top && (
              <Angabe symbol={Dokument} name="Vorgang">
                <button
                  className="aufgabenkarte__verweis"
                  onClick={() => {
                    aufSchliessen();
                    oeffne("sitzung", { topId: top.id, reiter: "vorlage" });
                  }}
                >
                  TOP {top.nummer} · {top.titel}
                  <PfeilRechts groesse={13} />
                </button>
              </Angabe>
            )}

            <button
              className="knopf knopf--schlicht knopf--breit aufgabenkarte__loeschen"
              onClick={async () => {
                await aktionen.loescheAufgabe(aufgabe.id);
                aufSchliessen();
              }}
            >
              Aufgabe löschen
            </button>
          </aside>
        </div>
      </div>
    </div>
  );
}

/* Eine Zeile in der rechten Spalte: Bezeichnung oben, Inhalt darunter. */
function Angabe({ symbol: Symbol, name, children }) {
  return (
    <div className="angabe">
      <p className="angabe__name">
        {Symbol && <Symbol groesse={14} />}
        {name}
      </p>
      <div className="angabe__wert">{children}</div>
    </div>
  );
}

/* ---------------------------------------------------------------
   Verantwortung — genau eine Person
   --------------------------------------------------------------- */

/*
  Eine Auswahlliste statt einer Reihe von Knöpfen. Eine Fraktion hat bis zu
  dreizehn Mitglieder; als Knopfreihe wäre das eine Wand, und die Auswahl
  öffnet auf dem iPad ohnehin das große Rad des Systems.
*/
function Verantwortung({ aufgabe, mitglieder }) {
  const { stammdaten, ich, aktionen } = useDemo();
  const wer = nameVon(stammdaten, aufgabe.zustaendigId, ich.id);

  return (
    <label className="verantwortung">
      <span className="verantwortung__kreis" aria-hidden="true">
        {kuerzel(wer)}
      </span>
      <span className="verantwortung__text">
        <span className="klein leise">Verantwortlich</span>
        <select
          className="verantwortung__wahl"
          value={aufgabe.zustaendigId || ""}
          onChange={(e) => aktionen.aendereAufgabe(aufgabe.id, { zustaendigId: e.target.value })}
          aria-label="Wer macht das?"
        >
          {mitglieder.map((person) => (
            <option key={person.id} value={person.id}>
              {nameVon(stammdaten, person.id, ich.id)}
            </option>
          ))}
        </select>
      </span>
    </label>
  );
}

/* ---------------------------------------------------------------
   Beobachtung — beliebig viele
   --------------------------------------------------------------- */

/*
  Wer eine Aufgabe anlegt, steht von selbst drin und kann sich austragen —
  genau das ging vorher nicht, als noch „wer hat sie angelegt“ darüber
  entschied.

  Die verantwortliche Person taucht hier nicht auf: Sie ist ohnehin
  beteiligt, und sie zusätzlich anzuhaken sähe nach einem Unterschied aus,
  den es nicht gibt.
*/
function Beobachtung({ aufgabe }) {
  const { stammdaten, ich, aktionen } = useDemo();
  const [offen, setzeOffen] = useState(false);

  const drin = (aufgabe.beobachterIds || []).filter((id) => id !== aufgabe.zustaendigId);
  const waehlbar = Object.values(stammdaten.personen).filter(
    (p) => p.id !== aufgabe.zustaendigId
  );

  function umschalten(personId) {
    const alle = aufgabe.beobachterIds || [];
    const neu = alle.includes(personId)
      ? alle.filter((id) => id !== personId)
      : [...alle, personId];
    return aktionen.aendereAufgabe(aufgabe.id, { beobachterIds: neu });
  }

  return (
    <>
      <div className="etiketten">
        {drin.map((id) => (
          <button
            key={id}
            className="etikett etikett--weg"
            onClick={() => umschalten(id)}
            title="Aus der Beobachtung nehmen"
          >
            {nameVon(stammdaten, id, ich.id)}
            <Kreuz groesse={12} />
          </button>
        ))}

        <button className="etikett etikett--mehr" onClick={() => setzeOffen(!offen)}>
          {offen ? "Fertig" : "+ Beobachter"}
        </button>
      </div>

      {offen && (
        <div className="wahl wahl--eng" style={{ marginTop: "var(--luft-2)" }}>
          {waehlbar.map((person) => (
            <button
              key={person.id}
              className="wahl__knopf"
              aria-pressed={drin.includes(person.id)}
              onClick={() => umschalten(person.id)}
            >
              {nameVon(stammdaten, person.id, ich.id)}
            </button>
          ))}
        </div>
      )}

      <p className="klein leise" style={{ marginTop: "var(--luft-2)" }}>
        Beobachten heißt: Die Aufgabe steht im eigenen Brett, ohne dafür verantwortlich zu
        sein.
      </p>
    </>
  );
}

/* „2026-08-06T16:00Z“ → „2026-08-06“, wie das Datumsfeld es erwartet. */
function alsFeld(datum) {
  if (!datum) return "";
  const d = new Date(datum);
  if (isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/* ---------------------------------------------------------------
   Etiketten — Wörter, keine Farben
   --------------------------------------------------------------- */

/*
  Bewusst ohne Farbe. Die Oberfläche hat schon zwei farbige Zeichensysteme,
  die begründet sind: den Textmarker und die Abstimmungsampel. Ein drittes
  würde die Parteineutralität aufweichen — und ein Etikett, das man nur an
  der Farbe erkennt, liest ein Achtel der Männer ohnehin falsch.
*/
function Etiketten({ aufgabe }) {
  const { aktionen } = useDemo();
  const [neu, setzeNeu] = useState("");

  const vorhanden = aufgabe.etiketten || [];

  async function ergaenzen() {
    const wort = neu.trim();
    if (!wort || vorhanden.includes(wort)) {
      setzeNeu("");
      return;
    }
    await aktionen.aendereAufgabe(aufgabe.id, { etiketten: [...vorhanden, wort] });
    setzeNeu("");
  }

  return (
    <div className="etiketten">
      {vorhanden.map((wort) => (
        <button
          key={wort}
          className="etikett etikett--weg"
          onClick={() =>
            aktionen.aendereAufgabe(aufgabe.id, {
              etiketten: vorhanden.filter((e) => e !== wort),
            })
          }
          title="Entfernen"
        >
          {wort}
          <Kreuz groesse={12} />
        </button>
      ))}

      <input
        className="brett__feld brett__feld--schmal"
        placeholder="Bauamt, eilig …"
        value={neu}
        onChange={(e) => setzeNeu(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && ergaenzen()}
        onBlur={ergaenzen}
        aria-label="Neues Etikett"
      />
    </div>
  );
}

/* ---------------------------------------------------------------
   Checkliste
   --------------------------------------------------------------- */

function Checkliste({ aufgabe }) {
  const { aktionen } = useDemo();
  const [neu, setzeNeu] = useState("");
  const punkte = aufgabe.checkliste || [];

  function sichere(neueListe) {
    return aktionen.aendereAufgabe(aufgabe.id, { checkliste: neueListe });
  }

  return (
    <>
      <p className="aufgabenkarte__frage">
        Schritte{" "}
        {punkte.length > 0 && `· ${punkte.filter((p) => p.erledigt).length} von ${punkte.length}`}
      </p>

      <ul className="checkliste">
        {punkte.map((punkt, i) => (
          <li
            key={i}
            className={`checkliste__punkt ${punkt.erledigt ? "checkliste__punkt--fertig" : ""}`}
          >
            <button
              className="aufgabe__haken"
              aria-pressed={punkt.erledigt}
              aria-label={punkt.erledigt ? "Als offen markieren" : "Als erledigt markieren"}
              onClick={() =>
                sichere(punkte.map((p, j) => (j === i ? { ...p, erledigt: !p.erledigt } : p)))
              }
            >
              <Haken groesse={16} />
            </button>
            <span style={{ flex: 1 }}>{punkt.text}</span>
            <button
              className="knopf knopf--schlicht"
              aria-label="Schritt entfernen"
              onClick={() => sichere(punkte.filter((p, j) => j !== i))}
            >
              <Kreuz groesse={14} />
            </button>
          </li>
        ))}
      </ul>

      <input
        className="brett__feld"
        placeholder="Schritt hinzufügen …"
        value={neu}
        onChange={(e) => setzeNeu(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" || !neu.trim()) return;
          sichere([...punkte, { text: neu.trim(), erledigt: false }]);
          setzeNeu("");
        }}
        aria-label="Neuer Schritt"
      />
    </>
  );
}
